import {
  useAppDispatch,
  useAppSelector,
} from "@/app/lib/features/redux/redux.hook";
import { useCallback } from "react";
import { sessionStorage } from "@/app/lib/storage/sessionStorage";
import {
  getCart,
  getCartCount,
  mergeCart,
  addToCart as addToCartThunk,
  updateCartQuantity as updateCartQuantityThunk,
  removeCartItem as removeCartItemThunk,
  clearCartThunk,
  toggleCartItem as toggleCartItemThunk,
  toggleSelectAll as toggleSelectAllThunk,
  selectBySeller as selectBySellerThunk,
  applyDiscount as applyDiscountThunk,
  removeDiscount as removeDiscountThunk,
} from "./cart.thunk";
import {
  resetCart,
  updateCartCount,
  setItemUpdating,
  setItemRemoving,
} from "./cart.slice";
import { isError, isIdle, isLoading, isSuccess } from "@/app/utils/asyncStatus";
import {
  AddToCartRequest,
  UpdateCartQuantityRequest,
  ToggleSelectAllRequest,
  SelectBySellerRequest,
  ApplyDiscountRequest,
} from "./cart.types";
import { trackAddToCart, trackRemoveFromCart } from "@/app/lib/tracking/events";
import useSettings from "@/app/lib/features/settings/settings.hook";

interface CartTrackingData {
  name: string;
  price: number;
  brand?: string;
  /** Satıcı/mağaza adı (pazaryeri) — GA4 item_brand DEĞİL, affiliation'a gider */
  affiliation?: string;
  category?: string;
}

// remove_from_cart için: sepet kalemi silinmeden önce okunan ürün bilgisi.
// itemId = ürünün kendi id'si (sepet satırı id'si değil) — GA4'te diğer
// ecommerce event'leriyle eşleşmesi gerekiyor.
interface RemoveTrackingData extends CartTrackingData {
  itemId: number;
  quantity?: number;
}

/**
 * Custom hook for cart operations
 * Provides all cart-related actions and state
 */
const useCart = () => {
  const dispatch = useAppDispatch();
  const { settings } = useSettings();
  const cart = useAppSelector((s) => s.cart.data);
  const cartError = useAppSelector((s) => s.cart.error);
  const cartStatus = useAppSelector((s) => s.cart.status);
  const cartCount = useAppSelector((s) => s.cart.count);
  const countStatus = useAppSelector((s) => s.cart.countStatus);
  const itemUpdating = useAppSelector((s) => s.cart.itemUpdating);
  const itemToggling = useAppSelector((s) => s.cart.itemToggling);
  const itemRemoving = useAppSelector((s) => s.cart.itemRemoving);
  const isTogglingAll = useAppSelector((s) => s.cart.isTogglingAll);
  const sellerToggling = useAppSelector((s) => s.cart.sellerToggling);
  const isApplyingDiscount = useAppSelector((s) => s.cart.isApplyingDiscount);
  const isRemovingDiscount = useAppSelector((s) => s.cart.isRemovingDiscount);
  const isClearingCart = useAppSelector((s) => s.cart.isClearingCart);

  /**
   * Fetch full cart data
   */
  const fetchCart = useCallback(async () => {
    const result = await dispatch(getCart());
    return result.meta.requestStatus === "fulfilled";
  }, [dispatch]);

  /**
   * Fetch cart item count only
   */
  const fetchCartCount = useCallback(async () => {
    const result = await dispatch(getCartCount());
    return result.meta.requestStatus === "fulfilled";
  }, [dispatch]);

  /**
   * Merge guest cart with user cart after login
   */
  const mergeCartData = useCallback(async () => {
    const result = await dispatch(mergeCart());
    if (result.meta.requestStatus === "fulfilled") {
      sessionStorage.clearSessionId();
      await dispatch(getCart());
    }
    return result.meta.requestStatus === "fulfilled";
  }, [dispatch]);

  /**
   * Add product to cart
   * @param skipRefresh - If true, doesn't automatically refresh cart (useful for batch operations)
   * @returns Object with success status and optional error message
   */
  const addToCart = useCallback(
    async (data: AddToCartRequest, skipRefresh = false, trackingData?: CartTrackingData): Promise<{ success: boolean; message?: string; price?: number }> => {
      const result = await dispatch(addToCartThunk(data));

      if (result.meta.requestStatus === "fulfilled") {
        // Sepete eklenen kalemin gerçek birim fiyatı (indirim uygulanmış hali).
        // Bu endpoint'in cart_item'ı GET /cart shape'inden farklı: price sayı
        // olarak dönüyor, tip ise string diyor — o yüzden tipe güvenmeden okuyoruz.
        // Okunamazsa çağıranın verdiği fiyata düşülür.
        const rawPrice = (
          result.payload as { data?: { cart_item?: { price?: unknown } } }
        )?.data?.cart_item?.price;
        const parsedPrice =
          typeof rawPrice === "number"
            ? rawPrice
            : typeof rawPrice === "string"
              ? Number(rawPrice)
              : NaN;
        const responsePrice = Number.isFinite(parsedPrice)
          ? parsedPrice
          : undefined;

        if (!skipRefresh) {
          // Bilinçli olarak await EDİLMİYOR: buton/modal "Ekleniyor..." durumunda
          // GET /cart bitene kadar takılı kalıyordu. Sepete ekleme POST cevabıyla
          // tamamlanmış olur; rozet sayısı da addToCart.fulfilled ile güncellenir.
          // Liste verisi (dropdown/sepet sayfası) birkaç yüz ms sonra Redux'a düşer.
          dispatch(getCart());
        }
        if (trackingData) {
          // Tracking'e API'nin döndüğü fiyat gider — ürün sayfasındaki fiyat
          // indirim öncesi (list_price) olabiliyor ve gelir raporlarını şişiriyordu.
          const unitPrice = responsePrice ?? trackingData.price;
          trackAddToCart(
            {
              value: unitPrice * data.quantity,
              items: [
                {
                  item_id: data.product_id,
                  // Meta/TikTok katalog kimliği = feed'deki g:id. Sepete
                  // eklerken zaten beden seviyesindeki variant_id'yi
                  // gönderiyoruz — g:id ile birebir aynı değer.
                  content_id: data.variant_id,
                  item_name: trackingData.name,
                  item_brand: trackingData.brand,
                  affiliation: trackingData.affiliation,
                  item_category: trackingData.category,
                  price: unitPrice,
                  quantity: data.quantity,
                },
              ],
            },
            settings?.google_ads_add_to_cart_label || undefined,
          );
        }

        return { success: true, price: responsePrice };
      }

      return {
        success: false,
        message: result.payload as string || "Ürün sepete eklenirken bir hata oluştu."
      };
    },
    [dispatch, settings],
  );

  /**
   * Add multiple products to cart in batch
   * Only refreshes cart once after all items are added
   * @returns Object with success count and error messages
   */
  const addMultipleToCart = useCallback(
    async (items: AddToCartRequest[], trackingItems?: CartTrackingData[]): Promise<{ successCount: number; errors: string[] }> => {
      let successCount = 0;
      const errors: string[] = [];
      const successfulItems: { req: AddToCartRequest; tracking?: CartTrackingData }[] = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const result = await dispatch(addToCartThunk(item));

        if (result.meta.requestStatus === "fulfilled") {
          successCount++;
          successfulItems.push({ req: item, tracking: trackingItems?.[i] });
        } else {
          const errorMessage = result.payload as string || "Ürün eklenirken hata oluştu";
          errors.push(errorMessage);
        }
      }

      if (successCount > 0) {
        // await yok — bkz. addToCart'taki not (UI POST'lar bitince serbest kalmalı)
        dispatch(getCart());

        const trackable = successfulItems.filter((s) => s.tracking);
        if (trackable.length > 0) {
          trackAddToCart(
            {
              value: trackable.reduce((sum, s) => sum + s.tracking!.price * s.req.quantity, 0),
              items: trackable.map((s) => ({
                item_id: s.req.product_id,
                // g:id karşılığı — tekli eklemeyle aynı kural
                content_id: s.req.variant_id,
                item_name: s.tracking!.name,
                item_brand: s.tracking!.brand,
                affiliation: s.tracking!.affiliation,
                item_category: s.tracking!.category,
                price: s.tracking!.price,
                quantity: s.req.quantity,
              })),
            },
            settings?.google_ads_add_to_cart_label || undefined,
          );
        }
      }

      return { successCount, errors };
    },
    [dispatch, settings],
  );

  /**
   * Update cart item quantity
   */
  const updateQuantity = useCallback(
    async (itemId: number, data: UpdateCartQuantityRequest): Promise<{ success: boolean; message?: string }> => {
      dispatch(setItemUpdating(itemId));

      const result = await dispatch(
        updateCartQuantityThunk({ itemId, data }),
      );

      dispatch(setItemUpdating(null));
      if (result.meta.requestStatus === "fulfilled") {
        return { success: true };
      }
      return {
        success: false,
        message: result.payload as string || "Miktar güncellenirken bir hata oluştu.",
      };
    },
    [dispatch],
  );

  /**
   * Remove item from cart
   */
  const removeItem = useCallback(
    async (itemId: number, trackingData?: RemoveTrackingData) => {
      dispatch(setItemRemoving(itemId));

      const result = await dispatch(removeCartItemThunk(itemId));

      dispatch(setItemRemoving(null));

      const success = result.meta.requestStatus === "fulfilled";

      // remove_from_cart yalnızca silme gerçekten başarılı olduğunda ve
      // tek sefer gönderilir. Tetikleme burada — tek çağrı noktası olduğu
      // için UI'da tekrar/çift push riski yok.
      if (success && trackingData) {
        const quantity = trackingData.quantity ?? 1;
        trackRemoveFromCart({
          value: trackingData.price * quantity,
          items: [
            {
              item_id: trackingData.itemId,
              item_name: trackingData.name,
              item_brand: trackingData.brand,
              affiliation: trackingData.affiliation,
              item_category: trackingData.category,
              price: trackingData.price,
              quantity,
            },
          ],
        });
      }

      return success;
    },
    [dispatch],
  );

  /**
   * Clear entire cart
   */
  const clearCart = useCallback(async () => {
    const result = await dispatch(clearCartThunk());
    
    if (result.meta.requestStatus === "fulfilled") {
      return true;
    }
    return false;
  }, [dispatch]);

  /**
   * Toggle cart item selection
   */
  const toggleItem = useCallback(
    async (itemId: number) => {
      const result = await dispatch(toggleCartItemThunk(itemId));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  /**
   * Toggle selection of all cart items
   */
  const toggleAll = useCallback(
    async (data: ToggleSelectAllRequest) => {
      const result = await dispatch(toggleSelectAllThunk(data));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  /**
   * Select/deselect all items from a specific seller
   */
  const toggleSeller = useCallback(
    async (data: SelectBySellerRequest) => {
      const result = await dispatch(selectBySellerThunk(data));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  /**
   * Apply discount code or coupon
   */
  const applyDiscount = useCallback(
    async (data: ApplyDiscountRequest) => {
      const result = await dispatch(applyDiscountThunk(data));
      const success = result.meta.requestStatus === "fulfilled";
      const message = (result.payload as { message?: string } | string | undefined);
      return {
        success,
        message: typeof message === "string" ? message : message?.message,
      };
    },
    [dispatch],
  );

  /**
   * Remove applied discount or coupon
   */
  const removeDiscountCode = useCallback(async () => {
    const result = await dispatch(removeDiscountThunk());
    return result.meta.requestStatus === "fulfilled";
  }, [dispatch]);

  /**
   * Reset cart state
   */
  const clearCartState = useCallback(() => {
    dispatch(resetCart());
  }, [dispatch]);

  /**
   * Manually set cart count
   */
  const setCartCount = useCallback(
    (count: number) => {
      dispatch(updateCartCount(count));
    },
    [dispatch],
  );

  /**
   * Get applied coupons from cart
   */
  const getAppliedCoupons = useCallback(() => {
    if (!cart?.applied_discounts) return [];
    return cart.applied_discounts
      .filter((d) => d.type === "coupon" && d.id)
      .map((d) => d.id as number);
  }, [cart]);

  return {
    // State
    cart,
    cartError,
    cartCount,
    cartStatus,
    countStatus,
    itemUpdating,
    itemToggling,
    itemRemoving,
    isTogglingAll,
    sellerToggling,
    isApplyingDiscount,
    isRemovingDiscount,
    isClearingCart,
    
    // Status helpers
    isIdle: isIdle(cartStatus),
    isLoading: isLoading(cartStatus),
    isSuccess: isSuccess(cartStatus),
    isError: isError(cartStatus),
    isCountLoading: isLoading(countStatus),
    
    // Actions
    fetchCart,
    fetchCartCount,
    mergeCartData,
    addToCart,
    addMultipleToCart, // Batch method with error handling
    updateQuantity,
    removeItem,
    clearCart,
    toggleItem,
    toggleAll,
    toggleSeller,
    applyDiscount,
    removeDiscountCode,
    clearCartState,
    setCartCount,
    getAppliedCoupons,
  };
};

export default useCart;